import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateRibbonsOptions,
  BulkCreateRibbonsResponse,
  BulkDeleteRibbonsResponse,
  BulkGetOrCreateRibbonsOptions,
  BulkGetOrCreateRibbonsResponse,
  BulkUpdateRibbonsOptions,
  BulkUpdateRibbonsResponse,
  GetOrCreateRibbonOptions,
  GetOrCreateRibbonResponse,
  GetRibbonOptions,
  MaskedRibbon,
  QueryRibbonsOptions,
  QueryRibbonsResponse,
  Ribbon,
  RibbonCreatedEnvelope,
  RibbonDeletedEnvelope,
  RibbonQuery,
  RibbonUpdatedEnvelope,
  RibbonsQueryBuilder,
  UpdateRibbon,
  UpdateRibbonOptions,
  bulkCreateRibbons as universalBulkCreateRibbons,
  bulkDeleteRibbons as universalBulkDeleteRibbons,
  bulkGetOrCreateRibbons as universalBulkGetOrCreateRibbons,
  bulkUpdateRibbons as universalBulkUpdateRibbons,
  createRibbon as universalCreateRibbon,
  deleteRibbon as universalDeleteRibbon,
  getOrCreateRibbon as universalGetOrCreateRibbon,
  getRibbon as universalGetRibbon,
  queryRibbons as universalQueryRibbons,
  typedQueryRibbons as universalTypedQueryRibbons,
  updateRibbon as universalUpdateRibbon,
} from './stores-catalog-v3-ribbon-ribbons-v-3.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/stores' };

export function createRibbon(httpClient: HttpClient): CreateRibbonSignature {
  return (ribbon: NonNullablePaths<Ribbon, `name`, 2>) =>
    universalCreateRibbon(
      ribbon,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateRibbonSignature {
  /**
   * Creates a ribbon.
   *
   * To assign the ribbon to a product, include the `ribbon.id` or `ribbon.name` when [creating](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/create-product) or [updating](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/update-product) a product.
   * @param - Ribbon to create.
   * @returns Created ribbon.
   */
  (ribbon: NonNullablePaths<Ribbon, `name`, 2>): Promise<
    NonNullablePaths<Ribbon, `name`, 2>
  >;
}

export function getRibbon(httpClient: HttpClient): GetRibbonSignature {
  return (ribbonId: string, options?: GetRibbonOptions) =>
    universalGetRibbon(
      ribbonId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRibbonSignature {
  /**
   * Retrieves a ribbon.
   * @param - Ribbon ID.
   * @returns Ribbon.
   */
  (ribbonId: string, options?: GetRibbonOptions): Promise<
    NonNullablePaths<Ribbon, `name`, 2>
  >;
}

export function updateRibbon(httpClient: HttpClient): UpdateRibbonSignature {
  return (
    _id: string,
    ribbon: NonNullablePaths<UpdateRibbon, `revision`, 2>,
    options?: UpdateRibbonOptions
  ) =>
    universalUpdateRibbon(
      _id,
      ribbon,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateRibbonSignature {
  /**
   * Updates a ribbon.
   *
   *
   * Each time the ribbon is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the ribbon.
   * This ensures you're working with the latest ribbon and prevents unintended overwrites.
   * @param - Ribbon ID.
   * @returns Updated Ribbon.
   */
  (
    _id: string,
    ribbon: NonNullablePaths<UpdateRibbon, `revision`, 2>,
    options?: UpdateRibbonOptions
  ): Promise<NonNullablePaths<Ribbon, `name`, 2>>;
}

export function deleteRibbon(httpClient: HttpClient): DeleteRibbonSignature {
  return (ribbonId: string) =>
    universalDeleteRibbon(
      ribbonId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteRibbonSignature {
  /**
   * Deletes a ribbon.
   *
   *
   * > **Note:** Deleting a ribbon will also remove it from all products it is assigned to.
   * @param - Ribbon ID.
   */
  (ribbonId: string): Promise<void>;
}

export function queryRibbons(httpClient: HttpClient): QueryRibbonsSignature {
  return (options?: QueryRibbonsOptions) =>
    universalQueryRibbons(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryRibbonsSignature {
  /**
   * Retrieves a list of up to 100 ribbons, given the provided filtering, sorting, and cursor paging.
   * Pass supported values to the `fields` array in the request to include those fields in the response.
   *
   *
   * Query Brands runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order
   * - `cursorPaging.limit` is `100`
   *
   * To learn about working with _Query_ endpoints, see
   * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
   * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
   */
  (options?: QueryRibbonsOptions): RibbonsQueryBuilder;
}

export function typedQueryRibbons(
  httpClient: HttpClient
): TypedQueryRibbonsSignature {
  return (query: RibbonQuery, options?: QueryRibbonsOptions) =>
    universalTypedQueryRibbons(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryRibbonsSignature {
  /** */
  (query: RibbonQuery, options?: QueryRibbonsOptions): Promise<
    NonNullablePaths<
      QueryRibbonsResponse,
      `ribbons` | `ribbons.${number}.name`,
      4
    >
  >;
}

export function bulkCreateRibbons(
  httpClient: HttpClient
): BulkCreateRibbonsSignature {
  return (
    ribbons: NonNullablePaths<Ribbon, `name`, 2>[],
    options?: BulkCreateRibbonsOptions
  ) =>
    universalBulkCreateRibbons(
      ribbons,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateRibbonsSignature {
  /**
   * Creates multiple ribbons.
   * @param - Ribbons to create.
   */
  (
    ribbons: NonNullablePaths<Ribbon, `name`, 2>[],
    options?: BulkCreateRibbonsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateRibbonsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateRibbons(
  httpClient: HttpClient
): BulkUpdateRibbonsSignature {
  return (
    ribbons: NonNullablePaths<
      MaskedRibbon,
      `ribbon._id` | `ribbon.revision`,
      3
    >[],
    options?: BulkUpdateRibbonsOptions
  ) =>
    universalBulkUpdateRibbons(
      ribbons,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateRibbonsSignature {
  /**
   * Updates multiple ribbons.
   *
   * Each time a ribbon is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating a ribbon.
   * This ensures you're working with the latest ribbon and prevents unintended overwrites.
   * @param - List of ribbons to update.
   */
  (
    ribbons: NonNullablePaths<
      MaskedRibbon,
      `ribbon._id` | `ribbon.revision`,
      3
    >[],
    options?: BulkUpdateRibbonsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateRibbonsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getOrCreateRibbon(
  httpClient: HttpClient
): GetOrCreateRibbonSignature {
  return (ribbonName: string, options?: GetOrCreateRibbonOptions) =>
    universalGetOrCreateRibbon(
      ribbonName,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOrCreateRibbonSignature {
  /**
   * Retrieves a ribbon by name, or creates a ribbon if one with the passed `ribbonName` doesn't exist.
   * @param - Ribbon name to retrieve or create.
   */
  (ribbonName: string, options?: GetOrCreateRibbonOptions): Promise<
    NonNullablePaths<GetOrCreateRibbonResponse, `ribbon.name`, 3>
  >;
}

export function bulkGetOrCreateRibbons(
  httpClient: HttpClient
): BulkGetOrCreateRibbonsSignature {
  return (ribbonNames: string[], options?: BulkGetOrCreateRibbonsOptions) =>
    universalBulkGetOrCreateRibbons(
      ribbonNames,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkGetOrCreateRibbonsSignature {
  /**
   * Retrieves multiple ribbons by name, or creates multiple ribbons if those with the passed `ribbonNames` don't exist.
   * @param - Ribbon names to retrieve or create.
   */
  (ribbonNames: string[], options?: BulkGetOrCreateRibbonsOptions): Promise<
    NonNullablePaths<
      BulkGetOrCreateRibbonsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteRibbons(
  httpClient: HttpClient
): BulkDeleteRibbonsSignature {
  return (ribbonIds: string[]) =>
    universalBulkDeleteRibbons(
      ribbonIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteRibbonsSignature {
  /**
   * Deletes multiple ribbons.
   * @param - IDs of ribbons to delete.
   */
  (ribbonIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteRibbonsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onRibbonCreated = EventDefinition(
  'wix.stores.catalog.v3.ribbon_created',
  true,
  (event: RibbonCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<RibbonCreatedEnvelope>();
export const onRibbonDeleted = EventDefinition(
  'wix.stores.catalog.v3.ribbon_deleted',
  true,
  (event: RibbonDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<RibbonDeletedEnvelope>();
export const onRibbonUpdated = EventDefinition(
  'wix.stores.catalog.v3.ribbon_updated',
  true,
  (event: RibbonUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<RibbonUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateRibbonsOptions,
  BulkCreateRibbonsRequest,
  BulkCreateRibbonsResponse,
  BulkDeleteRibbonsRequest,
  BulkDeleteRibbonsResponse,
  BulkGetOrCreateRibbonsForMigrationRequest,
  BulkGetOrCreateRibbonsForMigrationResponse,
  BulkGetOrCreateRibbonsOptions,
  BulkGetOrCreateRibbonsRequest,
  BulkGetOrCreateRibbonsResponse,
  BulkRibbonResult,
  BulkUpdateRibbonsOptions,
  BulkUpdateRibbonsRequest,
  BulkUpdateRibbonsResponse,
  CreateRibbonRequest,
  CreateRibbonResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  DeleteRibbonRequest,
  DeleteRibbonResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  File,
  GetOrCreateRibbonOptions,
  GetOrCreateRibbonRequest,
  GetOrCreateRibbonResponse,
  GetRibbonOptions,
  GetRibbonRequest,
  GetRibbonResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  MaskedRibbon,
  MessageEnvelope,
  Page,
  Pages,
  QueryRibbonsOptions,
  QueryRibbonsRequest,
  QueryRibbonsResponse,
  RecloneSiteRequest,
  RecloneSiteResponse,
  RequestedFields,
  RestoreInfo,
  Ribbon,
  RibbonCreatedEnvelope,
  RibbonDeletedEnvelope,
  RibbonQuerySpec,
  RibbonUpdatedEnvelope,
  RibbonsQueryBuilder,
  RibbonsQueryResult,
  SortOrder,
  Sorting,
  URI,
  URIs,
  UpdateRibbon,
  UpdateRibbonOptions,
  UpdateRibbonRequest,
  UpdateRibbonResponse,
  V3BulkRibbonResult,
  WebhookIdentityType,
  utils,
} from './stores-catalog-v3-ribbon-ribbons-v-3.universal.js';
